/**
 * W10 — what an `@` means: in the composer, and in a stored body.
 *
 * The api does not parse a body (docs/CONTRACT.md §4): a mention is a decision the client makes and
 * sends as `mentions: [ids]`, and the body only carries the `@handle` text for display. These are
 * the client's four decisions, all pure: which handle is being typed, what an insertion produces,
 * which members a body names, and which `@handle` runs in a body are *that member* rather than text
 * that merely looks like one.
 */
import type { UserView } from '@olmt/shared'

/** What a mention needs from a member: the id the api stores, and the handle a body spells. */
export type MentionMember = Pick<UserView, 'id' | 'handle'>

/** A run of a message body: ordinary text, or the `@handle` that names `member`. */
export type MentionSegment<T extends MentionMember = MentionMember> = {
  /** The run's own text, exactly as the sender typed it. */
  text: string
  /** The member this run names, or null for ordinary text. */
  member: T | null
}

/**
 * A handle's alphabet — the api's `[a-z0-9_]{3,32}` (`apps/api/src/ids.ts`), matched
 * case-insensitively because a reader may type `@Nadia`.
 */
const HANDLE_CHAR = /[a-z0-9_]/i

function isHandleChar(char: string | undefined): boolean {
  return char !== undefined && HANDLE_CHAR.test(char)
}

/**
 * The partial handle being typed at `caret`, or null when there is none. The empty string is a real
 * answer: an `@` on its own opens the picker with every member on offer.
 *
 * Null is also the answer for an `@` glued into a word (`email@nadia`) — an address is not a
 * mention, and a picker opening in the middle of one would be worse than no picker at all. Any
 * character outside the handle alphabet ends the query, so `@nadia.` is a finished mention and not
 * a picker still waiting for the sentence to continue.
 */
export function mentionQuery(text: string, caret: number): string | null {
  const end = Math.max(0, Math.min(caret, text.length))
  let start = end
  while (start > 0 && isHandleChar(text[start - 1])) start -= 1
  if (start === 0 || text[start - 1] !== '@') return null
  if (start >= 2 && isHandleChar(text[start - 2])) return null
  return text.slice(start, end)
}

/**
 * The text and the caret an insertion produces: the half-typed handle at `caret` is replaced by
 * `@handle` followed by one space. The space is the same rule that ends a query — it is what closes
 * the picker and what keeps the next word from being glued to the handle, so the mention is already
 * a finished token before the sender types a letter.
 *
 * With no query at the caret the token is inserted where the caret is, which is what an insertion
 * into ordinary text should do, and the text after the caret is kept either way.
 */
export function insertMention(
  text: string,
  caret: number,
  handle: string,
): { text: string; caret: number } {
  const end = Math.max(0, Math.min(caret, text.length))
  const query = mentionQuery(text, end)
  const start = query === null ? end : end - query.length - 1
  const token = `@${handle} `
  return { text: `${text.slice(0, start)}${token}${text.slice(end)}`, caret: start + token.length }
}

/**
 * The members the picker offers for `query`: the chat's members whose handle starts with it,
 * case-insensitively, in handle order — the list is a lookup of what is being typed, so it must not
 * reshuffle as the query grows. The empty query is every member, which is what a bare `@` asks for.
 * Handles are `[a-z0-9_]`, so code-point order is deterministic and locale-free.
 */
export function mentionCandidates<T extends MentionMember>(members: T[], query: string): T[] {
  const prefix = query.toLowerCase()
  return members
    .filter((member) => member.handle.toLowerCase().startsWith(prefix))
    .sort((a, b) => {
      const left = a.handle.toLowerCase()
      const right = b.handle.toLowerCase()
      if (left === right) return 0
      return left < right ? -1 : 1
    })
}

/** A member's handle in the form this module compares and slices: lowercased, with its length. */
type Candidate<T extends MentionMember> = { member: T; lower: string; length: number }

/**
 * The member whose `@handle` starts at `at`, or null. Candidates arrive longest handle first, so
 * when two members share a prefix the longer one wins; and the handle has to be a whole token —
 * `@nadia2` names `nadia2` and never `nadia`, an `@` glued to a word behind it (an address) names
 * nobody, and a handle character in front of that `@` is the same case.
 */
function candidateAt<T extends MentionMember>(
  text: string,
  at: number,
  candidates: Candidate<T>[],
): Candidate<T> | null {
  if (text[at] !== '@') return null
  if (isHandleChar(text[at - 1])) return null
  for (const candidate of candidates) {
    // The handle is compared case-insensitively without lowercasing the body: a copy of a body
    // could carry different indices (a character whose lowercase form is two code points), and the
    // runs below are slices of the body itself.
    let matched = true
    for (let index = 0; index < candidate.length; index += 1) {
      const char = text[at + 1 + index]
      if (char === undefined || char.toLowerCase() !== candidate.lower[index]) {
        matched = false
        break
      }
    }
    if (!matched) continue
    if (isHandleChar(text[at + 1 + candidate.length])) continue
    return candidate
  }
  return null
}

/**
 * A body split into its runs of ordinary text and the `@handle` runs that name a member — what the
 * bubble lights up, and what `mentionedHandles` reads. Case-insensitive; the runs come back in body
 * order and their text is the body's own, so the message is rendered exactly as it was typed.
 */
export function mentionSegments<T extends MentionMember>(
  body: string,
  members: T[],
): MentionSegment<T>[] {
  const candidates = members
    .map((member) => ({ member, lower: member.handle.toLowerCase(), length: member.handle.length }))
    .sort((a, b) => b.length - a.length)
  const segments: MentionSegment<T>[] = []
  let plain = 0
  let at = 0
  while (at < body.length) {
    const candidate = candidateAt(body, at, candidates)
    if (candidate === null) {
      at += 1
      continue
    }
    if (at > plain) segments.push({ text: body.slice(plain, at), member: null })
    const end = at + 1 + candidate.length
    segments.push({ text: body.slice(at, end), member: candidate.member })
    plain = end
    at = end
  }
  if (plain < body.length) segments.push({ text: body.slice(plain), member: null })
  return segments
}

/**
 * The members a body names, each of them once, longest handle first. This is the list the composer
 * sends as `mentions` — derived from the body at the moment of sending rather than remembered
 * beside it, so text that was backspaced out of a handle cannot leave a member named behind.
 *
 * Longest first because a shorter handle must never be read inside a longer one: a body holding
 * `@nadia2` names the member called `nadia2` and not a stray `2` after `nadia` — and when only
 * `nadia` is a member of the chat, `@nadia2` names nobody. Ties keep the chat's own member order,
 * for a list that is the same on every render.
 */
export function mentionedHandles<T extends MentionMember>(body: string, members: T[]): T[] {
  const named = new Set<string>()
  for (const segment of mentionSegments(body, members)) {
    if (segment.member !== null) named.add(segment.member.id)
  }
  if (named.size === 0) return []
  return members
    .filter((member) => named.has(member.id))
    .sort((a, b) => b.handle.length - a.handle.length)
}

/**
 * Whether this message names `meId`. The message's own `mentions` are the answer, not the body: a
 * member is named by the sender's decision, and only the ids the api stored can carry a push.
 */
export function mentionsMe(message: { mentions: string[] }, meId: string | null): boolean {
  return meId !== null && message.mentions.includes(meId)
}
